import { TitleCasePipe } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  ServiceRequestService,
  UserServiceRequestSummary,
} from '@core/services/service-request';
import { Gradientsegmentbar } from '@shared/components/gradientsegmentbar/gradientsegmentbar';
import { getStatusClass, getStatusDisplay } from '@shared/utilities/status.util';
import { fadeInLeftAnimation } from 'mdb-angular-ui-kit/animations';
import { MdbRippleModule } from 'mdb-angular-ui-kit/ripple';
import { MdbTableDirective, MdbTableModule } from 'mdb-angular-ui-kit/table';

@Component({
  selector: 'app-service-request-history',
  imports: [
    TitleCasePipe,
    Gradientsegmentbar,
    MdbRippleModule,
    MdbTableModule,
    RouterModule,
  ],
  templateUrl: './service-request-history.html',
  styleUrl: './service-request-history.scss',
  animations: [fadeInLeftAnimation()],
})
export class ServiceRequestHistory implements OnInit {
  @ViewChild('table') table!: MdbTableDirective<UserServiceRequestSummary>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly serviceRequestService = inject(ServiceRequestService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected readonly dataSource = signal<UserServiceRequestSummary[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly getStatusDisplay = getStatusDisplay;
  protected readonly getStatusClass = getStatusClass;
  protected readonly headers = ['Request #', 'Status', 'Description', 'Last Updated', 'View'];

  constructor() {
    // Preserve older links that opened details through a history query parameter.
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const id = Number(params.get('serviceRequestId'));
        if (Number.isInteger(id) && id > 0) {
          void this.router.navigate(['/service-request-details', id], { replaceUrl: true });
        }
      });
  }

  ngOnInit(): void {
    this.loadServiceRequests();
  }

  protected advancedSearch(value: string): void {
    this.table.search(value);
  }

  protected readonly filterFn = (
    data: UserServiceRequestSummary,
    searchTerm: string
  ): boolean => {
    const [phrase, columns] = searchTerm.split(' in:').map((value) => value.trim());

    return Object.keys(data).some((key) => {
      const value = data[key as keyof UserServiceRequestSummary];

      if (columns?.length) {
        return columns.split(',').some((column) =>
          column.toLowerCase().trim() === key.toLowerCase()
          && value?.toString().toLowerCase().includes(phrase.toLowerCase())
        );
      }

      return Boolean(
        value && JSON.stringify(data).toLowerCase().includes(phrase.toLowerCase())
      );
    });
  };

  protected formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Los_Angeles',
    });
  }

  protected formatServiceRequestId(serviceRequestId: number): string {
    return `SR-${serviceRequestId.toString().padStart(4, '0')}`;
  }

  private loadServiceRequests(): void {
    this.isLoading.set(true);
    this.serviceRequestService.getUserServiceRequests()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (requests) => {
          this.dataSource.set(requests);
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading service requests:', error);
          this.isLoading.set(false);
        },
      });
  }
}
